import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../../api/client';
import type { AdminCategory } from '../../types';

export function CategoriesPage() {
  const [rows, setRows] = useState<AdminCategory[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  async function reload() {
    try { setRows(await api('/api/admin/categories')); } catch (err) {
      setError(err instanceof Error ? err.message : 'Load failed');
    }
  }
  useEffect(() => { void reload(); }, []);

  async function onDelete(id: string, name: string) {
    if (!confirm(`Delete category "${name}" and ALL its phrases?`)) return;
    try { await api(`/api/admin/categories/${id}`, { method: 'DELETE' }); await reload(); }
    catch (err) { alert(err instanceof Error ? err.message : 'Failed'); }
  }

  return (
    <section className="admin-page">
      <header className="admin-page-header">
        <h1>Categories</h1>
        <Link to="/admin/categories/new" className="btn-primary">New category</Link>
      </header>
      {error && <div className="form-error">{error}</div>}
      {!rows && !error && <p style={{ color: 'var(--text-muted)' }}>Loading…</p>}
      {rows && (
        <table className="admin-table">
          <thead>
            <tr><th>Name</th><th>Slug</th><th>Icon</th><th>Sort</th><th>Free</th><th>Phrases</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id}>
                <td>{c.name}</td>
                <td><code>{c.slug}</code></td>
                <td>{c.icon}</td>
                <td>{c.sortOrder}</td>
                <td>{c.isFree ? '✓' : '—'}</td>
                <td>{c.phraseCount}</td>
                <td className="admin-table-actions">
                  <Link to={`/admin/categories/${c.id}`} className="btn-link">Edit</Link>
                  <button type="button" className="btn-link" onClick={() => void onDelete(c.id, c.name)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

interface CategoryForm {
  slug: string; name: string; description: string; icon: string; sortOrder: number; isFree: boolean;
}
const EMPTY: CategoryForm = { slug: '', name: '', description: '', icon: '', sortOrder: 0, isFree: false };

export function CategoryEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const [data, setData] = useState<CategoryForm>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isNew) return;
    void (async () => {
      try {
        const c = await api<CategoryForm>(`/api/admin/categories/${id}`);
        setData(c);
      } catch (err) { setError(err instanceof Error ? err.message : 'Load failed'); }
    })();
  }, [id, isNew]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const body = JSON.stringify(data);
      if (isNew) await api('/api/admin/categories', { method: 'POST', body });
      else await api(`/api/admin/categories/${id}`, { method: 'PUT', body });
      navigate('/admin/categories');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally { setSubmitting(false); }
  }

  return (
    <section className="admin-page">
      <header className="admin-page-header">
        <h1>{isNew ? 'New category' : `Edit category: ${data.name || ''}`}</h1>
      </header>
      {error && <div className="form-error">{error}</div>}
      <form onSubmit={(e) => void onSubmit(e)} className="admin-form">
        <div className="form-row-pair">
          <div className="form-group">
            <label>Slug</label>
            <input type="text" required value={data.slug} onChange={(e) => setData({ ...data, slug: e.target.value })} disabled={submitting} />
          </div>
          <div className="form-group">
            <label>Name</label>
            <input type="text" required value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} disabled={submitting} />
          </div>
        </div>
        <div className="form-group">
          <label>Description</label>
          <input type="text" value={data.description} onChange={(e) => setData({ ...data, description: e.target.value })} disabled={submitting} />
        </div>
        <div className="form-row-pair">
          <div className="form-group">
            <label>Icon (lucide-style name or emoji)</label>
            <input type="text" value={data.icon} onChange={(e) => setData({ ...data, icon: e.target.value })} disabled={submitting} />
          </div>
          <div className="form-group">
            <label>Sort order</label>
            <input type="number" value={data.sortOrder} onChange={(e) => setData({ ...data, sortOrder: Number(e.target.value) })} disabled={submitting} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-checkbox">
            <input type="checkbox" checked={data.isFree} onChange={(e) => setData({ ...data, isFree: e.target.checked })} disabled={submitting} />
            Free tier (legacy flag — used to seed the Free plan)
          </label>
        </div>
        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={() => navigate('/admin/categories')} disabled={submitting}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={submitting}>{submitting ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </section>
  );
}
